/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import { URL_BASE } from '../../constants/api_urls';
import axios from 'axios';
import Tree from 'react-d3-tree';
import { Button, Tooltip } from '@material-ui/core';
import funcTranslateHelper from '../../i18next/funcTranslateHelper';
import { makeStyles } from '@material-ui/core/styles';
import { useCenteredTree } from './helpers';
import { Loading } from 'carbon-components-react';
import PropTypes from 'prop-types';
const SHA256 = require('crypto-js/sha256');
export const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    wordWrap: 'break-word',
    textAlign: 'center',
    borderRadius: '5px',
    background: 'white',
    paddingLeft: '2px',
    paddingRight: '2px'

  },
  leaf: {
    padding: '4px',
    background: '#F96500',
    color: 'white'
  },
  dummy: {
    backgroundColor: '#d8d8d8'
  },
  isHashCalculated: {
    border: '2px solid #ee5b30'
  },
  fromProof: {
    backgroundColor: 'rgb(208 27 104)',
    color: 'white'
  },
  rootHash: {
    backgroundColor: 'white',
    border: '2px dashed #F96500'
  }

}));

const MerkleTreeVisualizer = ({ rootHash, txHash, setMerkle }) => {
  const [dimensions, translate, containerRef] = useCenteredTree();
  const [proof, setProof] = useState();
  const [isValid, setIsValid] = useState(true);
  const [merkleTree, setMerkleTree] = useState();
  const classes = useStyles();

  const leaf = txHash;
  const fetchProofData = async () => {
    const url = `${URL_BASE}/open/tx/${txHash}/proof/${rootHash}`;
    const data = await axios({ url: url })
      .then(res => res.data);
    setIsValid(data.isValid);
    setProof(data.proof);
  };
  useEffect(() => {
    fetchProofData();
  }, []);

  useEffect(() => {
    if (proof) {
      initializeMerkle(proof);
    }
  }, [proof]);
  let newMerkleTree;
  let largeMerkleTree = true;
  const initializeMerkle = (proof) => {
    const merkleTreeLayers = proof.length + 1;
    newMerkleTree = buildMerkleTree(merkleTreeLayers);
    // recorremos el arbol creado y asignamos las pruebad de merkle segun su posicion correpondiente
    assignProofToNode(newMerkleTree);
    // calculamos los hashes resultantes desde el txHash y los asignamos a los nodos correspondientes
    findAndProcessProofFromBottom(newMerkleTree);
    // asignamos valores a los nodos vacios
    changeDummyNames(newMerkleTree);
    setMerkleTree(newMerkleTree);
  };

  function createMerkleNode (value, children = [], position, type) {
    return {
      name: 'dummy',
      value,
      children,
      position,
      type
    };
  }
  // BUILD MERKLETREE
  function buildMerkleTree (layers) {
    if (layers < 1) {
      console.error('El número de layers debe ser al menos 1.');
      return null;
    }

    const root = createMerkleNode('Root');

    buildTreeRecursively(root, layers - 1);

    return root;
  }

  function buildTreeRecursively (node, remainingLayers) {
    if (remainingLayers === 0) {
      return;
    }
    // asignar root al primer nodo
    if (remainingLayers === proof.length) {
      node.name = 'root';
    }

    node.children = [
      createMerkleNode(`Level ${remainingLayers - 1}`, null, 'left', 'dummy'),
      createMerkleNode(`Level ${remainingLayers - 1}`, null, 'right', 'dummy')
    ];

    buildTreeRecursively(node.children[0], remainingLayers - 1);
    buildTreeRecursively(node.children[1], remainingLayers - 1);
  }

  function traverseMerkleTreeByLevel (node, targetLevel, proof) {
    if (!node) {
      return;
    }

    traverseNodeByLevel(node, null, targetLevel, 0, proof);
  }

  function traverseNodeByLevel (node, parent, targetLevel, currentLevel, individualProof) {
    if (!node) {
      return;
    }

    if (currentLevel === targetLevel) {
      if (node.position === individualProof.position) {
        if (individualProof.assigned) return;
        if (node.flag) return;

        node.name = individualProof.data.substring(0, 6) + '...';
        node.data = individualProof.data;
        node.type = 'fromProof';
        if (proof.length <= 5) {
          largeMerkleTree = false;
        }

        const inputString = node.value;
        const match = inputString.match(/\d+/);
        const number = match ? parseInt(match[0], 10) : NaN;
        if (largeMerkleTree) {
          if (number > Math.floor(proof.length / 3)) {
            delete node.children;
            node.children = [createMerkleNode(individualProof.data.substring(0, 6) + '...', [], null, 'dummy')];
          }
        }

        setFlagInHierarchy(node);
      }
    }

    if (node.children) {
      for (const child of node.children) {
        traverseNodeByLevel(child, node, targetLevel, currentLevel + 1, individualProof);
      }
    }
  }
  function setFlagInHierarchy (node) {
    if (!node) {
      return;
    }

    node.flag = true;

    if (node.children) {
      for (const child of node.children) {
        setFlagInHierarchy(child);
      }
    }
  }

  const assignProofToNode = (newMerkleTree) => {
    const numberOfLayers = proof.length;
    let proofNumber = proof.length - 1;
    for (let i = 1; i <= numberOfLayers; i++) {
      traverseMerkleTreeByLevel(newMerkleTree, i, proof[proofNumber]);
      proofNumber--;
    }
  };
  let dummyNameCounter = 0;
  function changeDummyNames (node) {
    if (node.children) {
      for (const child of node.children) {
        changeDummyNames(child);
      }
    }
    if (node.name === 'dummy') {
      if (node.value === 'Level 0') {
        dummyNameCounter++;
        node.name = dummyNameCounter;
      } else if (node.children.length) {
        node.name = `${node.children[0].name.toString()} ${node.children[1].name.toString()}`;
      } else if (!node.children.lenght) {
        node.name = node.value;
      }
    }
  }
  function findAndProcessProofFromBottom (node) {
    if (!node) {
      return;
    }

    if (node.children) {
      for (const child of node.children) {
        findAndProcessProofFromBottom(child);
      }
    }

    if (node.children && node.children.length > 0) {
      const nonDummyChild = node.children.find(child => child.name !== 'dummy');

      if (nonDummyChild) {
        fillSiblingName(node, nonDummyChild);

        hashNamesOfChildren(node);
      }
    }
  }

  function fillSiblingName (node, nonDummyChild) {
    const siblingNode = (nonDummyChild === node.children[0])
      ? node.children[1]
      : node.children[0];

    if (siblingNode.value === 'Level 0') {
      siblingNode.name = leaf.toString().substring(0, 6) + '...';
      siblingNode.data = leaf.toString();
      siblingNode.type = 'leaf';
    }
  }

  function hashNamesOfChildren (node) {
    const hashValue = hashFunction(node.children[0].data + node.children[1].data);
    node.name = node.value === 'Root' ? node.name = hashValue : hashValue.substring(0, 6) + '...';
    node.data = hashValue;
    node.type = 'isHashCalculated';
    if (node.value === 'Root') {
      node.type = 'rootHash';
    }
    return hashValue;
  }

  function hashFunction (value) {
    const newHash = SHA256(value).toString();
    return newHash;
  }

  const renderRectSvgNode = ({ nodeDatum, toggleNode }) => {
    const textContent = nodeDatum.name;
    let nodeWidth = 100;
    let nodeHeigth = 50;
    let toolTipName;
    switch (nodeDatum.type) {
      case 'leaf':
        nodeWidth = 100;
        nodeHeigth = 50;
        toolTipName = 'Tx_hash: ';
        break;
      case 'rootHash':
        nodeWidth = 300;
        nodeHeigth = 50;
        toolTipName = 'Root_hash: ';
        break;
    }

    return (
      <foreignObject
        x={-(nodeWidth / 2)}
        y={-(nodeHeigth / 2)}
        width={nodeWidth}
        height={nodeHeigth}
      >
        <div
          xmlns='http://www.w3.org/1999/xhtml' className={`${classes.root} ${classes[nodeDatum.type]}`}
        >
          <Tooltip
            title={toolTipName ? toolTipName + nodeDatum.data : nodeDatum.data || 'Hash'} style={{
              visibility: nodeDatum.data ? 'block' : 'none'
            }}
          >
            <p style={{ fontSize: '100%', width: '100%' }}>
              {!nodeDatum.data ? `H(${textContent})` : textContent}
            </p>
          </Tooltip>
        </div>
      </foreignObject>
    );
  };
  if (!proof) {
    return (
      <div style={{ height: '100vh', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading active withOverlay={false} />
      </div>
    );
  }
  function calculateZoom () {
    const baseLength = 14;
    const baseZoom = 0.3;

    const zoom = baseZoom * (baseLength / proof.length);

    const finalZoom = Math.max(0.1, Math.min(1, zoom));

    return finalZoom;
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ height: '80px', width: '100%', padding: '10px' }}>
        <Button variant='outlined' onClick={() => setMerkle(false)}>{funcTranslateHelper('Trazer.Merkletree.goBack')}</Button>
        {proof.isValid && <p>{funcTranslateHelper('Trazer.Merkletree.merkleproof')}</p>}
      </div>
      {merkleTree && isValid ? (
        <div id='treeWrapper' ref={containerRef} style={{ height: '70vh', width: '100%' }}>

          <Tree
            onNodeClick={(node) => console.log(node)}
            orientation='vertical'
            data={merkleTree}
            translate={translate}
            dimensions={dimensions}
            pathFunc='step'
            separation={{
              nonSiblings: 1,
              siblings: 0.8
            }}
            zoom={calculateZoom()}

            collapsible='false'
            renderCustomNodeElement={renderRectSvgNode}
          />
        </div>
      )
        : (
          <div style={{ height: '100vh', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p>{funcTranslateHelper('Trazer.Merkletree.invalidProof')}</p>
          </div>
        )}
    </div>
  );
};
MerkleTreeVisualizer.propTypes = {
  rootHash: PropTypes.string,
  txHash: PropTypes.string,
  setMerkle: PropTypes.func
};
export default MerkleTreeVisualizer;
